import { useCallback, useMemo, useState } from "react"
import { useQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetDeviceSourcesDocument,
  GetMappedAssetsDocument,
  GetSuggestedMappingsDocument,
  MapAssetToSourceDocument,
  BatchMapAssetsToSourcesDocument,
  UnmapAssetDocument,
  RefreshDeviceSourcesDocument,
} from "@hydrologiq/gql-experience/graphql"
import { emitSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"

export interface DeviceSource {
  ref: string
  label: string
  description: string | null
  deviceType: string
  compatibleClasses: string[]
  category: string | null
  /** Of the Assets passed to the hook, those core says may be mapped here. */
  fittingAssetIds?: string[] | null
  fields: string[] | null
}

export interface MappedAsset {
  id: string
  assetId: string
  assetName: string
  assetClass: string | null
  sourceRef: string
  sourceLabel: string
  deviceInstance: string | null
}

export interface SuggestedMapping {
  assetId: string
  assetName: string
  assetClass: string | null
  sourceRef: string
  sourceLabel: string
  confidence: string
}

export interface UseDataConnectionMappingReturn {
  // Discovery
  sources: DeviceSource[]
  loadingSources: boolean
  supportsRefresh: boolean
  discoveredAt: string | null
  connectorType: string
  stale: boolean
  refreshSources: () => Promise<boolean>
  refreshing: boolean

  // Mapping
  mappedAssets: MappedAsset[]
  loadingMapped: boolean
  mapAsset: (assetId: string, sourceRef: string) => Promise<void>
  mapAssetAndRefetch: (assetId: string, sourceRef: string) => Promise<void>
  batchMapAssets: (mappings: Array<{ assetId: string; sourceRef: string }>) => Promise<BatchMapResult>
  refetchMapped: () => Promise<void>
  unmapAsset: (assetIntegrationId: string) => Promise<void>
  mapping: boolean
  unmapping: boolean

  // Suggestions
  suggestedMappings: SuggestedMapping[]
  loadingSuggestions: boolean
  refetchSuggestions: () => Promise<void>
}

export interface BatchMapResultItem {
  id: string
  assetId: string
  assetName: string
  sourceRef: string
  sourceLabel: string
  success: boolean
  error: string | null
}

export interface BatchMapResult {
  results: BatchMapResultItem[]
  total: number
  succeeded: number
  failed: number
}

/** `assetIds` are the Assets the caller may map; core says which fit each source. */
export function useDataConnectionMapping(
  monitoringInstanceId: string,
  assetIds?: string[],
): UseDataConnectionMappingReturn {
  const [refreshing, setRefreshing] = useState(false)
  const [mapping, setMapping] = useState(false)
  const [unmapping, setUnmapping] = useState(false)
  const [refreshStale, setRefreshStale] = useState(false)

  const { data: sourcesData, loading: loadingSources, refetch: refetchSources } = useQueryWithEndpoint(
    GetDeviceSourcesDocument,
    {
      variables: assetIds ? { monitoringInstanceId, assetIds } : { monitoringInstanceId },
      skip: !monitoringInstanceId,
    },
    "primary",
  )

  const { data: mappedData, loading: loadingMapped, refetch: refetchMapped } = useQueryWithEndpoint(
    GetMappedAssetsDocument,
    {
      variables: { monitoringInstanceId },
      skip: !monitoringInstanceId,
    },
    "primary",
  )

  const { data: suggestionsData, loading: loadingSuggestions, refetch: refetchSuggestions } = useQueryWithEndpoint(
    GetSuggestedMappingsDocument,
    {
      variables: { monitoringInstanceId },
      skip: !monitoringInstanceId,
    },
    "primary",
  )

  const [mapAssetMutation] = useMutationWithEndpoint(
    MapAssetToSourceDocument,
    undefined,
    "primary",
  )

  const [unmapAssetMutation] = useMutationWithEndpoint(
    UnmapAssetDocument,
    undefined,
    "primary",
  )

  const [batchMapAssetsMutation] = useMutationWithEndpoint(
    BatchMapAssetsToSourcesDocument,
    undefined,
    "primary",
  )

  const [refreshSourcesMutation] = useMutationWithEndpoint(
    RefreshDeviceSourcesDocument,
    undefined,
    "primary",
  )

  // Stable refetchMapped wrapper to avoid re-creating callbacks
  const refetchMappedStable = useCallback(async () => {
    await refetchMapped()
  }, [refetchMapped])

  // Refetch suggestions (needed after removing mappings so next dialog open shows updated suggestions)
  const refetchSuggestionsStable = useCallback(async () => {
    await refetchSuggestions()
  }, [refetchSuggestions])

  const refreshSources = useCallback(async (): Promise<boolean> => {
    setRefreshing(true)
    try {
      const { data, errors } = await refreshSourcesMutation({
        variables: { monitoringInstanceId },
      })
      if (errors?.length) {
        throw new Error(errors[0].message)
      }
      // Track whether the refresh returned stale data (discovery failed)
      const isStale = data?.refreshDeviceSources?.stale ?? false
      setRefreshStale(isStale)
      await refetchSources()
      await refetchMapped()
      return isStale
    } finally {
      setRefreshing(false)
    }
  }, [refreshSourcesMutation, monitoringInstanceId, refetchSources, refetchMapped])

  const mapAsset = useCallback(
    async (assetId: string, sourceRef: string) => {
      setMapping(true)
      try {
        const { errors } = await mapAssetMutation({
          variables: { input: { monitoringInstanceId, assetId, sourceRef } },
        })
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
      } finally {
        setMapping(false)
      }
    },
    [mapAssetMutation, monitoringInstanceId],
  )

  const mapAssetAndRefetch = useCallback(
    async (assetId: string, sourceRef: string) => {
      await mapAsset(assetId, sourceRef)
      await refetchMapped()
      emitSectionEvent(SECTION_EVENTS.ASSET_MAPPED)
    },
    [mapAsset, refetchMapped],
  )

  const unmapAsset = useCallback(
    async (assetIntegrationId: string) => {
      setUnmapping(true)
      try {
        const { errors } = await unmapAssetMutation({
          variables: { assetIntegrationId },
        })
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
        await refetchMapped()
        await refetchSuggestions()
      } finally {
        setUnmapping(false)
      }
    },
    [unmapAssetMutation, refetchMapped, refetchSuggestions],
  )

  const batchMapAssets = useCallback(
    async (mappings: Array<{ assetId: string; sourceRef: string }>): Promise<BatchMapResult> => {
      setMapping(true)
      try {
        const { data, errors } = await batchMapAssetsMutation({
          variables: {
            input: { monitoringInstanceId, mappings },
          },
        })
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
        const result = data?.batchMapAssetsToSources
        if (!result) {
          throw new Error("No result returned from batch mapping")
        }
        await refetchMapped()
        emitSectionEvent(SECTION_EVENTS.ASSET_MAPPED)
        return result as BatchMapResult
      } finally {
        setMapping(false)
      }
    },
    [batchMapAssetsMutation, monitoringInstanceId, refetchMapped],
  )

  const sources = useMemo(
    () => (sourcesData?.getDeviceSources?.sources ?? []) as DeviceSource[],
    [sourcesData],
  )
  const supportsRefresh = sourcesData?.getDeviceSources?.supportsRefresh ?? false
  const discoveredAt = sourcesData?.getDeviceSources?.discoveredAt ?? null
  const connectorType = sourcesData?.getDeviceSources?.connectorType ?? ""
  const queryStale = sourcesData?.getDeviceSources?.stale ?? false
  // stale is true if either the initial query reported stale or a refresh returned stale
  const stale = queryStale || refreshStale
  const mappedAssets = useMemo(
    () => (mappedData?.getMappedAssets ?? []) as MappedAsset[],
    [mappedData],
  )
  const suggestedMappings = useMemo(
    () => (suggestionsData?.getSuggestedMappings ?? []) as SuggestedMapping[],
    [suggestionsData],
  )

  return {
    // Discovery
    sources,
    loadingSources,
    supportsRefresh,
    discoveredAt,
    connectorType,
    stale,
    refreshSources,
    refreshing,

    // Mapping
    mappedAssets,
    loadingMapped,
    mapAsset,
    mapAssetAndRefetch,
    batchMapAssets,
    refetchMapped: refetchMappedStable,
    unmapAsset,
    mapping,
    unmapping,

    // Suggestions
    suggestedMappings,
    loadingSuggestions,
    refetchSuggestions: refetchSuggestionsStable,
  }
}
